import "./index.css";

import React from "react";
import BaseComponent from 'Core/components/BaseComponent';
import PropTypes from "prop-types";
import {connect} from 'react-redux';
import * as pageConfig from '../../config';
import {selectors} from 'Core/store/reducers';
import {reducerStoreKey} from '../../reducer';
import {icon_font_error_symbol} from 'Config/app';
import {getArray} from 'Core/helpers/data';
import Spinner from 'Core/components/display/Spinner';
import Label from 'Core/components/display/Label';
import CheckboxInput from 'Core/components/input/CheckboxInput';
import {orderBy} from 'lodash';
import {SIGNAL_QUALITY_GRADE_CLASS_NAME} from '../../const';
import {Tooltip} from 'Core/components/global/Tooltip';
import {updateSignalQualityAction} from '../../actions';
import {getPageActions} from 'Core/helpers/redux';
import MainTableSignalQualityReports from '../MainTableSignalQualityReports';

/**
 * Define all actions used by this component.
 */
const allActions = getPageActions({updateSignalQualityAction});

/**
 * Redux 'mapStateToProps' function
 *
 * @param {object} state - Redux entire store state.
 * @param {Object} ownProps - Components own props.
 * @return {Object<string, any>} Mapped props that can be used in the component.
 */
const mapStateToProps = (state, ownProps) => ({
	/** @type {?SignalQualityDataObject|undefined} */
	signalQuality: selectors[reducerStoreKey].getStudySignalQualityData(state, ownProps.studyId),
});

class MainTableSignalQuality extends BaseComponent {
	constructor(props) {
		super(props, {
			translationPath: pageConfig.translationPath,
			domPrefix: 'study-signal-quality',
			optimizedUpdate: true,
			optimizedUpdateIgnoreProps: [...Object.keys(allActions)],
		});

		// Action methods
		this.updateReviewed = this.updateReviewed.bind(this);
		this.handleReviewedClick = this.handleReviewedClick.bind(this);
	}
	
	
	// Action methods ---------------------------------------------------------------------------------------------------
	/**
	 * Update reviewed status
	 *
	 * @param {boolean} reviewed - New reviewed status.
	 * @param {string} studyPreliminaryNightId
	 * @param {string} studyNightId
	 * @param {number} night
	 * @return {Promise<SignalQualityDataObject|undefined>}
	 */
	updateReviewed(reviewed, studyPreliminaryNightId, studyNightId, night) {
		const {studyId, updateSignalQualityAction} = this.props;
		const loading = this.showLoading(`#${this.getDomId()} .night-${night}`);
		
		return this.executeAbortableAction(
			updateSignalQualityAction, studyId, studyPreliminaryNightId, studyNightId, reviewed
		)
			.finally(() => this.hideLoading(loading));
	}
	
	/**
	 * Handle reviewed checkbox click
	 * @param {boolean} checked - Whether the checkbox is checked.
	 * @param {SignalQualityNightDataObject} studyNight - The study night.
	 */
	handleReviewedClick(checked, studyNight) {
		this.updateReviewed(checked, studyNight.preliminaryId, studyNight.id, studyNight.night).then();
	}


	// Render methods ---------------------------------------------------------------------------------------------------
	render() {
		const {studyId, readOnly} = this.props;
		/** @type {?SignalQualityDataObject|undefined} */
		const signalQuality = this.getProp('signalQuality');
		const nights = orderBy(getArray(signalQuality, 'studyNights'), ['night'], ['asc']);
		
		return (
			<div id={this.getDomId()} className={`${this.getOption('domPrefix')}`}>
				{
					signalQuality === null ?
						<Spinner />
					: typeof signalQuality === 'undefined' ?
						<Label 
							icon={icon_font_error_symbol} 
							iconClassName="error-color" 
							tooltip={this.t('signalQualityError')}
						/>
					: nights.length === 0 ?
						<Label content="—" tooltip={this.t('noSignalQualityData')} />
					:
						<div className="nights">
							{nights.map(
								/** @param {SignalQualityNightDataObject} studyNight */
								studyNight => (
									studyNight.grade !== null && studyNight.grade > 0 ?
										<Tooltip
											key={`${studyNight.night}`}
											tag="span"
											title={
												this.tt(
													`GRADE_${studyNight.grade}`, 'signalQualityGradeTooltip', 
													'', {night: studyNight.night}
												) + '<br />' + this.t(
													(studyNight.reviewed ? 'signalQualityReviewed' : 'signalQualityNotReviewed')
												)
											}
											size="small"
											position="top-center"
											arrow={true}
											interactive={false}
										>
											<CheckboxInput
												readOnly={readOnly}
												className={
													`${SIGNAL_QUALITY_GRADE_CLASS_NAME[studyNight.grade]} night-${studyNight.night}`
												}
												size={18}
												checkedIcon="check-square"
												uncheckedIcon="square"
												checked={studyNight.reviewed}
												onChange={v => this.handleReviewedClick(v, studyNight)}
											/>
										</Tooltip>
										: null
								)
							)}
						</div>
				}

				{signalQuality !== null ?
					<Tooltip
						tag="div"
						title={`${this.t('signal_quality_reports_dropdown_label')} ...`}
						size="small"
						position="top-center"
						arrow={true}
						interactive={false}
					>	
						<MainTableSignalQualityReports studyId={studyId} disabled={readOnly} />
					</Tooltip>
					: null
				}
			</div>
		);
	}
}

/**
 * Define a component's own props that can be passed to it by parent components
 */
MainTableSignalQuality.propTypes = {
	studyId: PropTypes.string.isRequired,
	readOnly: PropTypes.bool,
};

export default connect(mapStateToProps, allActions, null, {forwardRef: true})(MainTableSignalQuality);